import { Clock, Play } from "lucide-react";
import { useEffect, useState } from "react";
import { Button } from "@/components/Button";
import { Chip } from "@/components/Chip";
import { EmptyState } from "@/components/EmptyState";
import { ErrorState } from "@/components/ErrorState";
import { LoadingState } from "@/components/LoadingState";
import { Modal, ModalTitle } from "@/components/Modal";
import { Pagination } from "@/components/Pagination";
import { SearchField } from "@/components/SearchField";
import { Select } from "@/components/Select";
import { useApi } from "@/lib/api";
import { formatDateTime } from "@/lib/formatDateTime";
import { formatDuration } from "@/lib/formatDuration";
import { PAGE_SIZE, type PagedResult } from "@/lib/pagination";
import { useDebouncedValue } from "@/lib/useDebouncedValue";
import {
  type KubernetesRequestSummary,
  type KubernetesRecordingSummary,
  MAX_REQUEST_COUNT,
  REQUEST_TYPE_OPTIONS,
  ResourceName,
  StatusChip,
  Verb,
} from "./kubernetesShared";
import { ReplayModal } from "./SshRecordings";

const COLUMNS = ["When", "Verb", "Resource", "Namespace", "Status"] as const;

const OPEN_FOR_THRESHOLD_MS = 5_000;

export function KubernetesRecordingModal({
  orgId,
  recordingId,
  onClose,
}: {
  orgId: string;
  recordingId: string;
  onClose: () => void;
}) {
  const api = useApi();
  const basePath = `/organizations/${orgId}/kubernetes-recordings/${recordingId}`;

  const [recording, setRecording] = useState<KubernetesRecordingSummary | null>(
    null,
  );
  const [requests, setRequests] = useState<KubernetesRequestSummary[] | null>(
    null,
  );
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [recordingError, setRecordingError] = useState<string | null>(null);
  const [requestsError, setRequestsError] = useState<string | null>(null);
  const [playing, setPlaying] = useState<KubernetesRequestSummary | null>(null);

  const [search, setSearch] = useState("");
  const debouncedSearch = useDebouncedValue(search, 300);
  const [type, setType] = useState("");

  useEffect(() => {
    let cancelled = false;

    void api.get<KubernetesRecordingSummary>(basePath).then((data) => {
      if (cancelled) return;

      if (data) {
        setRecording(data);
      } else {
        setRecordingError("Couldn't load the recording.");
      }
    });

    return () => {
      cancelled = true;
    };
  }, [api, basePath]);

  useEffect(() => {
    let cancelled = false;

    const params = new URLSearchParams({
      page: String(page),
      pageSize: String(PAGE_SIZE),
    });

    if (debouncedSearch) params.set("search", debouncedSearch);
    if (type) params.set("type", type);

    void api
      .get<PagedResult<KubernetesRequestSummary>>(
        `${basePath}/requests?${params.toString()}`,
      )
      .then((data) => {
        if (cancelled) return;

        if (data) {
          setRequests(data.items);
          setTotalCount(data.totalCount);
          setRequestsError(null);
        } else {
          setRequestsError("Couldn't load the requests.");
        }
      });

    return () => {
      cancelled = true;
    };
  }, [api, basePath, page, debouncedSearch, type]);

  const error = recordingError ?? requestsError;
  const hasFilters = Boolean(debouncedSearch || type);
  const pageCapped =
    totalCount > (page - 1) * PAGE_SIZE + MAX_REQUEST_COUNT;

  // Replace the list modal to avoid competing focus traps and Escape handlers.
  // Keep this component mounted so closing playback restores its filters and page.
  if (playing?.recordingId && recording) {
    return (
      <ReplayModal
        orgId={orgId}
        recording={{
          id: playing.recordingId,
          resourceName: recording.resourceName,
          requestedByUserName: recording.requestedByUserName,
          requestedByUserEmail: recording.requestedByUserEmail,
          requestedAt: playing.startedAt,
          truncated: playing.recordingTruncated,
        }}
        onClose={() => setPlaying(null)}
      />
    );
  }

  return (
    <Modal onClose={onClose} maxWidth="max-w-5xl" closeOnBackdropClick={false}>
      <div className="mb-4 shrink-0">
        <ModalTitle>
          {recording?.resourceName ?? "Kubernetes recording"}
        </ModalTitle>

        {recording && (
          <p className="flex flex-wrap items-center gap-2 text-sm text-foreground/70">
            <span>
              {recording.requestedByUserName ??
                recording.requestedByUserEmail ??
                "Unknown"}{" "}
              · {formatDateTime(recording.requestedAt)}
            </span>
            {recording.recordingIncomplete && (
              <Chip variant="warning">Partially recorded</Chip>
            )}
          </p>
        )}
      </div>

      <div className="mb-4 flex shrink-0 flex-wrap gap-2">
        <SearchField
          value={search}
          onChange={(value) => {
            setSearch(value);
            setPage(1);
          }}
          label="Search requests"
          placeholder="Search resource or namespace"
        />
        <Select
          aria-label="Requests"
          value={type}
          onChange={(e) => {
            setType(e.target.value);
            setPage(1);
          }}
        >
          {REQUEST_TYPE_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </Select>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto pr-1">
        {error ? (
          <ErrorState message={error} />
        ) : !requests ? (
          <LoadingState />
        ) : requests.length === 0 ? (
          <EmptyState
            title={
              hasFilters
                ? "No requests match these filters"
                : "No requests recorded"
            }
            description={
              hasFilters ? "Try a different search or request type." : ""
            }
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[48rem] text-left text-sm">
              <thead>
                <tr>
                  {COLUMNS.map((column) => (
                    <th
                      key={column}
                      scope="col"
                      className="border-b border-surface pb-2.5 pr-4 text-xs font-semibold uppercase tracking-wider text-foreground/70"
                    >
                      {column}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {requests.map((request) => (
                  <tr
                    key={request.id}
                    className={`h-14 border-b border-surface/70 ${request.recordingId ? "cursor-pointer hover:bg-surface" : ""}`}
                    onClick={
                      request.recordingId
                        ? () => setPlaying(request)
                        : undefined
                    }
                  >
                    <td className="whitespace-nowrap py-2 pr-4 tabular-nums text-foreground/70">
                      {formatDateTime(request.startedAt)}
                    </td>
                    <td className="py-2 pr-4">
                      <Verb request={request} />
                    </td>
                    <td className="py-2 pr-4 wrap-anywhere">
                      <ResourceName request={request} />
                      <ShellRecording request={request} />
                      <OpenFor request={request} />
                    </td>
                    <td className="whitespace-nowrap py-2 pr-4 text-foreground/70">
                      {request.namespace ?? "Cluster-wide"}
                    </td>
                    <td className="whitespace-nowrap py-2 pr-4">
                      <StatusChip statusCode={request.statusCode} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {requests && requests.length > 0 && (
        <div className="shrink-0">
          <Pagination
            page={page}
            totalPages={Math.ceil(totalCount / PAGE_SIZE)}
            capped={pageCapped}
            onPageChange={setPage}
          />
        </div>
      )}

      <div className="mt-4 flex shrink-0 justify-end">
        <Button onClick={onClose}>Close</Button>
      </div>
    </Modal>
  );
}

function OpenFor({ request }: { request: KubernetesRequestSummary }) {
  if (request.durationMs < OPEN_FOR_THRESHOLD_MS) {
    return null;
  }

  return (
    <>
      {" "}
      <Chip>
        <Clock className="h-2.5 w-2.5" strokeWidth={2.5} aria-hidden="true" />
        Open {formatDuration(request.durationMs / 1000)}
      </Chip>
    </>
  );
}

function ShellRecording({ request }: { request: KubernetesRequestSummary }) {
  if (request.recordingId) {
    return (
      <>
        {" "}
        <Chip variant="accent">
          <Play className="h-2.5 w-2.5" strokeWidth={2.5} aria-hidden="true" />
          Play
        </Chip>
        {request.recordingTruncated && (
          <>
            {" "}
            <Chip variant="warning">Incomplete recording</Chip>
          </>
        )}
      </>
    );
  }

  if (
    (request.verb !== "exec" && request.verb !== "attach") ||
    request.recording === "file-copy" ||
    request.recording === "no-output"
  ) {
    return null;
  }

  return (
    <>
      {" "}
      <Chip>Not recorded</Chip>
    </>
  );
}
